import createCache from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import { currentEnvironment } from "@porsche-customer/slfinrtl-environment"
import { PorscheDesignSystemProvider, PToast } from "@porsche-design-system/components-react"
// eslint-disable-next-line no-restricted-imports
import React, * as ReactAll from "react"
import ReactDOM, * as ReactDOMAll from "react-dom"
import * as ReactIntlAll from "react-intl"
import { createBrowserRouter, RouterProvider } from "react-router"
import { DealerAppRouter } from "./DealerAppRouter"
import { getFeatureToggle } from "./common/getConfig"
import { GlobalErrorBoundary } from "./components/globalErrorBoundary/GlobalErrorBoundary"
import { useDash0 } from "./hooks/useDash0/useDash0"
import { usePwaInstall } from "./hooks/usePwaInstall/usePwaInstall"
import { PocLightningOutLoginCallbackContainer } from "./lazyContainers"
import { DealershipListContainer } from "./screens/dealershipList/DealershipListContainer"
import { isNewsPath } from "./screens/newsList/newsListPath"
import { POC_LIGHTNING_OUT_LOGIN_CALLBACK_PATH } from "./screens/vehicleReservation/salesforcePoc/lightningOut/pocLightningSalesforceRedirectUrl"

// https://emotion.sh/docs/@emotion/cache
const emotionCache = createCache({
	key: "p",
	...(process.env.NODE_ENV === "development" && { stylisPlugins: [] }), // disable vendor prefixer in dev mode
})

export const BaseApp = () => {
	const environment = ReactAll.useMemo(() => currentEnvironment(), [])
	useDash0(environment ?? "local")
	usePwaInstall()

	ReactAll.useEffect(() => {
		const S = window.System
		S.set("dep:react", { default: React, ...ReactAll })
		S.set("dep:react-dom", { default: ReactDOM, ...ReactDOMAll })
		S.set("dep:react-intl", ReactIntlAll)
	}, [])

	const dealerParams = ReactAll.useMemo(() => {
		const pathname = window.location.pathname
		const segment = pathname.split("/")
		const isOrganizationsLevelPath = getFeatureToggle("news-enabled") && isNewsPath(pathname)
		return {
			dealershipId: isOrganizationsLevelPath ? undefined : segment[1],
			locale: segment[2] as Locale | undefined,
		}
	}, [])

	return (
		<CacheProvider value={emotionCache}>
			<PorscheDesignSystemProvider prefix="dealer-app">
				<GlobalErrorBoundary>
					{/* TODO FRD-4711 remove after Salesforce PoC */}
					{window.location.pathname === POC_LIGHTNING_OUT_LOGIN_CALLBACK_PATH ? (
						<RouterProvider
							router={createBrowserRouter([
								{
									element: <PocLightningOutLoginCallbackContainer />,
									path: POC_LIGHTNING_OUT_LOGIN_CALLBACK_PATH,
								},
							])}
						/>
					) : dealerParams.dealershipId ? (
						<DealerAppRouter dealershipId={dealerParams.dealershipId} locale={dealerParams.locale} />
					) : (
						<RouterProvider
							router={createBrowserRouter([
								{
									element: <DealershipListContainer />,
									path: "/*",
								},
							])}
						/>
					)}
					<PToast />
				</GlobalErrorBoundary>
			</PorscheDesignSystemProvider>
		</CacheProvider>
	)
}
